import './cart-rewards.css';

import EventEmitter from 'eventemitter3';
import { LoyaltyProvider, Modals, ContentType, Pages } from '../../../../../types/enums';
import { Data } from '../../../../../models/data';
import { Order } from '../../../../../models/order';
import { EventTypes } from '../../../../../core/event-bus';
import { Util } from '../../../../../utils/util';
import { ICartRewardReferences } from './cart-rewards.interface';
import { IUserRewardOffersDTO, IUserNextOrderCredit } from '../../../../../models/user.interface';
import { ProfileService } from '../../../../../services/profile-service';
import { DataLayerService } from '../../../../../services/data-layer-service';
import { Context } from '../../../../../models/context';
import { LocalizationService } from '../../../../../services/localization-service';
import { ArrowScroller } from '../../../../shared/ArrowScroller/arrow-scroller';
import { Router } from '../../../../../core/router';

/**
 * Manages the Cart Rewards section, handling user loyalty and available offers.
 */
export class CartRewards {
    private readonly _containerElement: HTMLElement;
    private readonly _containerReferences: ICartRewardReferences;
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _localizationService: LocalizationService;
    private readonly _profileService: ProfileService;
    private readonly _dataLayer: DataLayerService;
    private readonly _order: Order;
    private readonly _arrowScroller: ArrowScroller;
    private _isRenderStateChanging: boolean = false;

    /**
     * Initializes the CartRewards component.
     * @param {EventEmitter} eventBus - The event bus for handling events.
     * @param {Data} data - The service managing data-related operations.
     * @param {ProfileService} profileService - The service managing user-related operations.
     */
    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, profileService: ProfileService, dataLayer: DataLayerService, order: Order) {
        this._eventBus = eventBus;
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;
        this._profileService = profileService;
        this._dataLayer = dataLayer;
        this._order = order;
        this._containerElement = this._createHtmlContainer();
        this._containerReferences = this._createReferences();
        this._arrowScroller = new ArrowScroller(this._containerReferences.offersCards, { localizationService: this._localizationService });

        this._setupEventBusListeners();
    }

    /**
     * Gets the main HTML container for the Cart Rewards component.
     */
    public get element(): HTMLElement {
        return this._containerElement;
    }

    /**
     * Initializes the Cart Rewards component.
     */
    public async init(): Promise<void> {
        await this._renderComponent();
    }

    /**
     * Sets up event listeners for the event bus.
     */
    private _setupEventBusListeners() {
        this._eventBus.on(EventTypes.CART_CHANGED, async () => { await this._handleCartChange(); });
        this._eventBus.on(EventTypes.LOGGED_IN, async () => { await this._handleUserAuthStatusChange(); });
        this._eventBus.on(EventTypes.LOGGED_OUT, async () => { await this._handleUserAuthStatusChange(); });
        this._eventBus.on(EventTypes.LANGUAGE_CHANGED, async () => { await this._renderComponent(); });
    }

    /**
     * Creates the main HTML container for the Cart Rewards section.
     * @returns The generated HTML element.
     */
    private _createHtmlContainer(): HTMLElement {
        const sectionContainer = document.createElement('div');
        sectionContainer.classList.add('cart-reward-section');
        const header = document.createElement('h2');
        header.textContent = this._localizationService.names("MyRewards");
        header.setAttribute("ltag", "MyRewards");
        const spanTxt = document.createElement('span');
        spanTxt.id = 'spn_cart_loyalty_text';

        const loyaltyButtonsDiv = document.createElement('div');
        loyaltyButtonsDiv.innerHTML = `
            <div id="div_cart_loyalty_buttons" class="cart-reward-sign-in">
                <button id="btn_cart_loyalty_login" ltag="SignIn" class="secondary">${this._localizationService.names("SignIn")}</button>
                <button id="btn_cart_loyalty_signup" ltag="SignUp">${this._localizationService.names("SignUp")}</button>
            </div>
        `;

        const loyaltyOffersDiv = document.createElement('div');
        loyaltyOffersDiv.innerHTML = `
            <div id="div_cart_loyalty_offers" class="cart-reward-parent">
                <h3 ltag="MyOffers">${this._localizationService.names("MyOffers")}</h3>
                <span id="span_cart_loyalty_offers_none">${this._localizationService.names("NO_OFFERS")}</span>
                <div id="div_cart_loyalty_offers_cards" class="cart-reward-cards"></div>
            </div>
        `;

        const nextOrderCreditsDiv = document.createElement('div');
        nextOrderCreditsDiv.innerHTML = `
            <div id="div_cart_loyalty_credits" class="cart-reward-parent">
                <h3>${this._localizationService.names("MyCredits")}</h3>
                <div id="div_cart_loyalty_credits_cards" class="cart-credits-cards"></div>
            </div>
        `;

        sectionContainer.append(header, spanTxt, loyaltyButtonsDiv, loyaltyOffersDiv, nextOrderCreditsDiv);

        // Set Event Listeners
        const logInButton = sectionContainer.querySelector("#btn_cart_loyalty_login") as HTMLButtonElement;
        if (logInButton) {
            logInButton.onclick = () => this._handleLogInClick();
        }

        const signUpButton = sectionContainer.querySelector("#btn_cart_loyalty_signup") as HTMLButtonElement;
        if (signUpButton) {
            signUpButton.onclick = () => this._handleSignUpClick();
        }

        return sectionContainer;
    }

    /**
     * Toggles the visibility of the cart loyalty section based on the loyalty provider's availability.
     */
    private _showElement(): void {
        const isHidden = this._data.getLoyaltyInfo().providerId === LoyaltyProvider.NONE;
        const action = isHidden ? Util.hideElement : Util.showElement;
        action(this._containerElement);
    }

    /**
     * Creates references to various UI elements.
     * @returns An object containing references to key UI elements.
     */
    private _createReferences(): ICartRewardReferences {
        return {
            span: this._containerElement.querySelector(`#spn_cart_loyalty_text`) as HTMLSpanElement,
            buttonsSection: this._containerElement.querySelector(`#div_cart_loyalty_buttons`) as HTMLElement,
            offersSection: this._containerElement.querySelector(`#div_cart_loyalty_offers`) as HTMLElement,
            offersNoneSpan: this._containerElement.querySelector(`#span_cart_loyalty_offers_none`) as HTMLSpanElement,
            offersCards: this._containerElement.querySelector(`#div_cart_loyalty_offers_cards`) as HTMLElement,
            creditsSection: this._containerElement.querySelector(`#div_cart_loyalty_credits`) as HTMLElement,
            creditsCards: this._containerElement.querySelector(`#div_cart_loyalty_credits_cards`) as HTMLElement
        };
    }

    /**
     * Handles cart change event and updates the UI accordingly
     */
    private readonly _handleCartChange = async (): Promise<void> => {
        await this._renderComponent();
    }

    /**
     * Handles user login/logout event and updates the UI accordingly.
     */
    private readonly _handleUserAuthStatusChange = async (): Promise<void> => {
        await this._renderComponent();
    }

    /**
     * Handles login button click event.
     */
    private readonly _handleLogInClick = (): void => {
        this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.LOGIN);
    }

    /**
     * Handles signup button click event.
     */
    private readonly _handleSignUpClick = (): void => {
        this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.SIGNUP);
    }

    /**
     * Renders the Cart Rewards component based on user login status.
     */
    private async _renderComponent(): Promise<void> {
        if (Router.path !== Pages.CART || this._isRenderStateChanging)
            return;

        this._isRenderStateChanging = true;

        try {
            this._showElement();

            if (this._profileService.user.isLoggedIn())
                await this._renderLoggedInState();
            else
                await this._renderLoggedOutStateAsync();
        } finally {
            this._isRenderStateChanging = false;
        }
    }

    /**
     * Updates UI when the user is logged in.
     */
    private async _renderLoggedInState(): Promise<void> {
        const pointsUnit = this._localizationService.getLoyaltyUnitName(this._data.getLoyaltyInfo()).toLowerCase();
        const items = this._order.getProperty("Items");
        if (items.length === 0) {
            this._containerReferences.span.innerHTML = this._localizationService.names("EARN_REWARDS_ON_ORDER_NO_ITEMS")
                .replaceAll("{{points_unit}}", pointsUnit);
        } else {
            const points = await this._profileService.loyaltyProvider.getPointsAsync();
            this._containerReferences.span.innerHTML = this._localizationService.names("EARN_REWARDS_ON_ORDER")
                .replaceAll("{{points}}", points.toLocaleString(this._context.getProperty("languageCode")))
                .replaceAll("{{points_unit}}", pointsUnit);
        }

        Util.hideElement(this._containerReferences.buttonsSection);

        Util.showElement(this._containerReferences.offersSection);
        const offers = this._profileService.user.getProperty("Offers") || null;
        const rewards = this._profileService.user.getProperty("LoyaltyData")?.Rewards || [];
        const combinedOffers = await this._profileService.combineUserOffers(offers, rewards, true);

        this._containerReferences.offersCards.innerHTML = "";
        this._containerReferences.creditsCards.innerHTML = "";

        if (combinedOffers.length > 0) {
            Util.hideElement(this._containerReferences.offersNoneSpan);
            for (const offer of combinedOffers) {
                const element = this._buildCard(offer);
                if (element)
                    this._containerReferences.offersCards.appendChild(element);
            }
            Util.showElement(this._containerReferences.offersCards);
            this._arrowScroller.update();
        } else {
            Util.showElement(this._containerReferences.offersNoneSpan);
            Util.hideElement(this._containerReferences.offersCards);
        }

        this._renderCreditsSection();
    }

    /**
     * Updates UI when the user is logged out.
     */
    private async _renderLoggedOutStateAsync(): Promise<void> {
        const items = this._order.getProperty("Items");
        const loyaltyInfo = this._data.getLoyaltyInfo();
        const pointsUnit = this._localizationService.getLoyaltyUnitName(loyaltyInfo);

        if (items.length === 0) {
            this._containerReferences.span.innerHTML = this._localizationService.names("EARN_REWARDS_SIGNUP_NO_ITEMS")
                .replaceAll("{{points_unit}}", pointsUnit);
        } else {
            const points = await this._profileService.loyaltyProvider.getPointsAsync();
            this._containerReferences.span.innerHTML = this._localizationService.names("EARN_REWARDS_SIGNUP")
                .replaceAll("{{points}}", points.toString())
                .replaceAll("{{points_unit}}", pointsUnit);
        }
        
        Util.showElement(this._containerReferences.buttonsSection);
        Util.hideElement(this._containerReferences.offersSection);
        this._containerReferences.offersCards.innerHTML = "";
        Util.hideElement(this._containerReferences.creditsSection);
        this._containerReferences.creditsCards.innerHTML = "";
    }

    /**
     * Builds a reward or offer card element.
     * @param {IUserRewardOffersDTO} offer - The offer or reward data.
     * @returns {HTMLElement | null} - The h4 heading wrapping the card, or null if the offer is already used.
     */
    private _buildCard(offer: IUserRewardOffersDTO): HTMLElement | null {
        if (offer.Used)
            return null;

        const name = this._localizationService.GetName(offer.Names);
        const html = `
            <div id="btn_cart_loyalty_offer_card_${offer.OfferId}" class="cart-reward-card" role="button" tabindex="0">
                <div class="title">
                    ${name}
                </div>
                ${offer.ExpiryDate ? `<div class="expiry">${this._localizationService.names("Expires")} ${Util.formatDate(offer.ExpiryDate)}</div>` : ""}
            </div>`;

        const card = Util.createHtmlElementFromTemplate(html);
        card.onclick = async () => { await this._handleCardClickAsync(offer); };

        card.setAttribute("aria-label", name);

        // An <h4> inside role="button" is flattened by some screen readers, so the heading wraps the card instead.
        const heading = document.createElement("h4");
        heading.classList.add("cart-reward-card-heading");
        heading.appendChild(card);
        return heading;
    }

    private _renderCreditsSection(): void {
        if (!this._data.getBrandProperty("Settings").OOS.showNextOrderCredit) {
            Util.hideElement(this._containerReferences.creditsSection);
            return;
        }

        //sort by expiration date, then by customer credit ID if expiration date is the same
        const credits = (this._profileService.user.getProperty("NextOrderCredits")?.filter(c => c.StoreID === this._data.getProperty("StoreInfo").storeId && !c.UsedDate) || [])
            .sort((a, b) => {
                if (a.ExpirationDate === null && b.ExpirationDate !== null) {
                    return 1;
                } else if (a.ExpirationDate !== null && b.ExpirationDate === null) {
                    return -1;
                } else if (a.ExpirationDate !== null && b.ExpirationDate !== null && a.ExpirationDate !== b.ExpirationDate) {
                    return new Date(a.ExpirationDate).getTime() - new Date(b.ExpirationDate).getTime();
                } else {
                    return a.CustomerCreditID > b.CustomerCreditID ? -1 : 1;
                }
            });
        if (credits.length > 0) {
            for (const credit of credits) {
                const element = this._buildCreditsCard(credit);
                if (element)
                    this._containerReferences.creditsCards.appendChild(element);
            }

            if (this._containerReferences.creditsCards.children.length > 0)
                Util.showElement(this._containerReferences.creditsSection);
            else
                Util.hideElement(this._containerReferences.creditsSection);
        } else {
            Util.hideElement(this._containerReferences.creditsSection);
        }
    }

    /**
     * Builds a next order credit card element.
     * @param {IUserNextOrderCredit} credit - The credit data.
     * @returns {HTMLElement | null} - The created card element or null if the credit is expired or has no valid associated coupon.
     */
    private _buildCreditsCard(credit: IUserNextOrderCredit): HTMLElement | null {
        if (credit.ExpirationDate != null && new Date(credit.ExpirationDate) < Util.getNowStore(this._data))
            return null;

        const creditCoupon = this._order.couponGetCoupon(credit.CouponID, true, this._order.getProperty("OrderTypeID"), null);
        if (!creditCoupon)
            return null;

        const buttonName = credit.Used ? "Void" : "Redeem";

        const html = `
            <div id="btn_cart_loyalty_credit_card_${credit.CustomerCreditID}" class="cart-reward-credit-card" tabindex="0">
                <div class="cart-reward-credit-card-base-row">
                    <div class="cart-reward-credit-card-base-text">
                        <strong>${this._localizationService.GetName(creditCoupon.Names)}</strong>
                        <span class="expiry">${this._localizationService.names("Expires")} ${Util.formatDate(credit.ExpirationDate)}</span>
                    </div>
                    <div class="card-reward-credit-card-button-action">
                        <button id="btn_credits_redeem_${creditCoupon.CouponId}" name="btn_credits_redeem" data-noccid="${creditCoupon.CouponId}" class="cart-reward-credit-redeem-button m0">${this._localizationService.names(buttonName)}</button>
                    </div>
                </div>
            </div>
        `;

        const element = Util.createHtmlElementFromTemplate(html);
        element.onclick = async () => {
            const offerDto: IUserRewardOffersDTO = {
                OfferId: credit.CustomerCreditID.toString(),
                IsReward: false,
                IsCredit: true,
                ExpiryDate: credit.ExpirationDate,
                Used: true,
                Names: creditCoupon.Names
            }
            await this._handleCardClickAsync(offerDto);
        };
        return element;
    }

    /**
     * Handles the click event on a reward, offer, or next order credit card.
     * @param {IUserRewardOffersDTO} offer - The offer or reward or credit data that was clicked.
     */
    private readonly _handleCardClickAsync = async (offer: IUserRewardOffersDTO): Promise<void> => {
        const offerId = !offer.IsReward && !offer.IsCredit ? offer.OfferId : undefined;
        const rewardId = offer.IsReward ? offer.OfferId : undefined;
        const creditId = offer.IsCredit ? offer.OfferId : undefined;

        const result = await this._profileService.redeemOfferAsync(rewardId, offerId, creditId);
        if (result.isUsed)
            await this._renderLoggedInState();

        if (this._dataLayer && !creditId && (offerId || rewardId)) {
            let rewardName = '';
            if (rewardId)
                rewardName = this._localizationService.GetName(this._profileService.user.getProperty("LoyaltyData")?.Rewards.find(x => x.RewardId === Number(rewardId))?.Names ?? []);
            if (offerId) {
                const couponId = this._profileService.user.getProperty("Offers")?.Codes?.find(x => x.CouponCode.toLowerCase() === offerId.toLowerCase())?.CouponId;
                if (couponId)
                    rewardName = this._localizationService.GetName(this._profileService.user.getProperty("Offers")?.Coupons.find(x => x.CouponId === couponId)?.Names.map(x => ({ CULT: x.lc, NAM: x.text })) ?? []);
            }

            this._dataLayer.rewards_redeem(rewardName, offerId ? ContentType.OFFER : ContentType.POINTS, this._data.getProperty("StoreInfo").storeCode.toString());
        }
    }
}